import { useState, useEffect, useCallback, useRef } from "react";

// Desbloqueo con Face ID / huella al abrir la app, igual que en apps
// bancarias (CaixaBank y similares). Usa la Web Authentication API
// (WebAuthn) con el autenticador de la propia plataforma (Face ID en
// iOS, huella/cara en Android, Windows Hello / Touch ID en escritorio),
// que es el estándar web para pedir biometría del dispositivo — no hay
// forma de acceder a la huella o cara "en crudo" desde una web, ni falta
// que hace: el sistema operativo hace la verificación y solo nos dice
// si el usuario ha pasado o no.
//
// IMPORTANTE — qué es y qué no es esto: la cuenta se sigue protegiendo
// con el email/contraseña de Firebase Auth de siempre. Este bloqueo es
// una capa extra LOCAL, en este dispositivo y este navegador: cuando
// activas Face ID/huella, se genera una credencial WebAuthn ligada a
// este aparato y se guarda su identificador en este navegador. A partir
// de ahí, cada vez que se abre o se vuelve a la app se pide esa
// verificación antes de dejar ver nada, sin tener que volver a escribir
// la contraseña. Si entras desde otro móvil, borras los datos del
// navegador, o el propio navegador expulsa el almacenamiento local del
// sitio (Safari en iOS lo hace tras semanas sin visitar el sitio), hay
// que activarlo de nuevo — es justo lo mismo que pasa con Face ID en
// cualquier app bancaria si cambias de teléfono.
//
// No depende de ningún servidor: no hace falta Cloud Functions ni tocar
// Firebase Auth. Si el aparato no tiene sensor biométrico (la mayoría
// de ordenadores no lo tienen), "supported" sale false y la opción
// simplemente no se muestra.
//
// DISEÑO — localStorage como única fuente de verdad: Layout.js (quien
// muestra la pantalla de bloqueo) y Ajustes (quien tiene el interruptor)
// usan cada uno su PROPIA instancia de este hook — son componentes
// distintos, no comparten estado de React entre sí. Por eso, en vez de
// fiarnos del estado "enabled" de cada instancia (que puede quedarse
// desactualizado respecto a lo que hizo la OTRA instancia), cada
// comprobación de bloqueo relee localStorage directamente en el momento,
// que sí es compartido de verdad entre ambos. Así, aunque el aviso entre
// instancias (CHANGE_EVENT, más abajo) llegue tarde o se pierda, el
// bloqueo real sigue funcionando porque no depende de él.

const STORAGE_PREFIX = "aria_biolock_";
// Avisa a todas las instancias del hook abiertas en la misma pestaña en
// cuanto se activa/desactiva desde cualquiera de ellas — solo se usa
// para refrescar el interruptor visualmente al instante, NUNCA es la
// única vía por la que se decide si hay que bloquear (ver diseño arriba).
const CHANGE_EVENT = "aria_biolock_changed";

function bufToB64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function b64ToBuf(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export function useBiometricLock(uid, email) {
  const [supported, setSupported] = useState(false);
  const [checkingSupport, setCheckingSupport] = useState(true);
  const storageKey = uid ? `${STORAGE_PREFIX}${uid}` : null;
  const hasCred = useCallback(() => !!(storageKey && localStorage.getItem(storageKey)), [storageKey]);

  // Se calcula de forma síncrona en el primer render (localStorage.getItem
  // no es asíncrono) para que, si ya hay una credencial activada, la app
  // arranque bloqueada desde el primer pintado.
  const [enabled, setEnabled] = useState(hasCred);
  const [locked, setLocked] = useState(hasCred);
  const [verifying, setVerifying] = useState(false);
  const [lastError, setLastError] = useState("");

  // Detecta si el navegador soporta WebAuthn con autenticador de
  // plataforma (condición necesaria para que Face ID/huella tenga
  // sentido aquí — si no, ni mostramos la opción en Ajustes).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (window.PublicKeyCredential && PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
          const ok = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          if (!cancelled) setSupported(!!ok);
        }
      } catch {
        if (!cancelled) setSupported(false);
      } finally {
        if (!cancelled) setCheckingSupport(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Si "uid" no estaba listo todavía en el primer render (la sesión de
  // Firebase tarda un instante en restaurarse), esto vuelve a comprobar
  // en cuanto storageKey pasa a tener un valor real, y bloquea de
  // inmediato si ya había una credencial guardada.
  useEffect(() => {
    const isEnabled = hasCred();
    setEnabled(isEnabled);
    if (isEnabled) setLocked(true);
  }, [storageKey, hasCred]);

  // Se entera al instante si OTRA instancia del hook (p.ej. el interruptor
  // en Ajustes) activó o desactivó el bloqueo, para refrescar su propio
  // "enabled" sin esperar a una recarga. El bloqueo real de la pantalla
  // no depende de esto (ver el efecto de abajo, que relee localStorage
  // directamente en cada evento de segundo plano).
  useEffect(() => {
    if (!storageKey) return;
    function onChange(e) {
      if (e?.detail?.uid && e.detail.uid !== uid) return; // otro usuario, ignorar
      const isEnabled = hasCred();
      setEnabled(isEnabled);
      if (!isEnabled) setLocked(false);
    }
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, [storageKey, uid, hasCred]);

  // Bloquea la app en cuanto la pestaña/app pasa a segundo plano —
  // relee localStorage EN EL MOMENTO (no el estado "enabled" de este
  // componente, que podría no haberse enterado todavía de un cambio
  // hecho desde otra instancia) para no depender de que ese aviso haya
  // llegado a tiempo.
  //
  // En vez de fiarnos solo de detectar el momento en que la app se va a
  // segundo plano ("visibilitychange"/"blur"/"pagehide" — en Android,
  // minimizar la app no siempre dispara estos eventos de forma fiable,
  // según el navegador y la versión), se bloquea también al detectar
  // que la app VUELVE a primer plano ("focus" y sobre todo "pageshow",
  // que es justo el evento pensado para cuando una pestaña se restaura
  // desde la caché de retroceso tras haber estado en segundo plano — es
  // el más fiable de todos en móvil). Con las dos direcciones cubiertas,
  // basta con que UNA sola señal se dispare, en cualquiera de los dos
  // sentidos, para que quede bloqueado.
  //
  // "justUnlockedAtRef" evita que el propio cuadro nativo de huella/Face
  // ID (que también hace perder y recuperar el foco de la ventana al
  // abrirse y cerrarse) vuelva a bloquear la pantalla justo después de
  // haberla desbloqueado con éxito.
  const justUnlockedAtRef = useRef(0);
  useEffect(() => {
    if (!storageKey) return;
    function lockIfEnabled() {
      if (hasCred()) setLocked(true);
    }
    function relockIfEnabled() {
      if (Date.now() - justUnlockedAtRef.current < 1500) return;
      lockIfEnabled();
    }
    function onVisibility() {
      if (document.hidden) lockIfEnabled(); else relockIfEnabled();
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", lockIfEnabled);
    window.addEventListener("blur", lockIfEnabled);
    window.addEventListener("focus", relockIfEnabled);
    window.addEventListener("pageshow", relockIfEnabled);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", lockIfEnabled);
      window.removeEventListener("blur", lockIfEnabled);
      window.removeEventListener("focus", relockIfEnabled);
      window.removeEventListener("pageshow", relockIfEnabled);
    };
  }, [storageKey, hasCred]);

  // Red de seguridad para el modo "icono instalado en pantalla de inicio"
  // de Android: en ese modo concreto, en algunos móviles NINGUNO de los
  // eventos de arriba (visibilitychange/blur/pagehide/focus/pageshow) se
  // llega a disparar al minimizar y reabrir la app — es una limitación
  // del propio modo "standalone" en ese navegador/versión, confirmada
  // porque el mismo código sí funciona en una pestaña normal de Chrome y
  // en iPhone. Como red de seguridad que no depende de ningún evento del
  // navegador, se usa un "latido": cada segundo se mira cuánto tiempo
  // real ha pasado desde el latido anterior. Mientras la app está en
  // segundo plano, el hilo de JavaScript se congela por completo — no
  // hay ningún setInterval "perdido" ni contado de más — así que en
  // cuanto se reanuda, ese primer latido llega con un hueco de varios
  // segundos de golpe (mucho mayor que el segundo esperado), y eso basta
  // para saber que la app estuvo en segundo plano y bloquear, sin haber
  // necesitado que se disparase ningún evento de visibilidad.
  useEffect(() => {
    if (!storageKey) return;
    let lastTick = Date.now();
    const HEARTBEAT_MS = 1000;
    const STALE_THRESHOLD_MS = 2500;
    const interval = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTick;
      lastTick = now;
      if (gap > STALE_THRESHOLD_MS && hasCred()) {
        if (Date.now() - justUnlockedAtRef.current > 1500) setLocked(true);
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [storageKey, hasCred]);

  const enable = useCallback(async () => {
    if (!uid) return { error: "no-user" };
    setLastError("");
    try {
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const userId = crypto.getRandomValues(new Uint8Array(16));
      const cred = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: { name: "ARIA Wallet" },
          user: { id: userId, name: email || uid, displayName: email || "ARIA Wallet" },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 },   // ES256
            { type: "public-key", alg: -257 }, // RS256
          ],
          authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
          timeout: 60000,
          attestation: "none",
        },
      });
      if (!cred) return { error: "cancelled" };
      localStorage.setItem(storageKey, bufToB64(cred.rawId));
      // Se comprueba que de verdad se haya guardado (por si el navegador
      // bloquea localStorage, p.ej. en una pestaña privada) — si no se
      // pudo guardar, no tiene sentido decir que está "activado".
      if (!hasCred()) {
        return { error: "no-storage" };
      }
      setEnabled(true);
      setLocked(false);
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { uid } }));
      return { ok: true };
    } catch (err) {
      console.error("No se pudo activar Face ID/huella:", err);
      setLastError(err?.message || String(err));
      return { error: err?.message || String(err) };
    }
  }, [uid, email, storageKey, hasCred]);

  const disable = useCallback(() => {
    if (storageKey) localStorage.removeItem(storageKey);
    setEnabled(false);
    setLocked(false);
    if (uid) window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { uid } }));
  }, [storageKey, uid]);

  // Pide la verificación biométrica. Un "true" solo puede venir de que
  // el sistema operativo haya confirmado la cara/huella/PIN del
  // dispositivo contra la credencial creada en enable() — si el usuario
  // cancela, falla la biometría, o la credencial no existe (dispositivo
  // nuevo), rechaza y se queda bloqueado.
  const verify = useCallback(async () => {
    if (!storageKey) return false;
    const credB64 = localStorage.getItem(storageKey);
    if (!credB64) return false;
    setVerifying(true);
    setLastError("");
    try {
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge,
          allowCredentials: [{ type: "public-key", id: b64ToBuf(credB64) }],
          userVerification: "required",
          timeout: 60000,
        },
      });
      setVerifying(false);
      if (assertion) { justUnlockedAtRef.current = Date.now(); setLocked(false); return true; }
      return false;
    } catch (err) {
      console.warn("Verificación biométrica no completada:", err);
      setLastError(err?.message || String(err));
      setVerifying(false);
      return false;
    }
  }, [storageKey]);

  return { supported, checkingSupport, enabled, locked, verifying, lastError, enable, disable, verify };
}
